import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'

// Drag the corner handle of a dashboard panel to resize it. The panel
// snaps to the preset widths/heights (grid spans) while dragging and the
// chosen size is persisted on release; the server replaces the panel.
const WIDTH_SPANS = { third: 2, half: 3, two_thirds: 4, full: 6 }
const HEIGHT_SPANS = { sm: 1, md: 2, lg: 3, xl: 4 }
const WIDTH_CLASSES = {
  third: 'lg:col-span-2',
  half: 'lg:col-span-3',
  two_thirds: 'lg:col-span-4',
  full: 'lg:col-span-6'
}
const HEIGHT_CLASSES = {
  sm: 'lg:row-span-1',
  md: 'lg:row-span-2',
  lg: 'lg:row-span-3',
  xl: 'lg:row-span-4'
}
const DRAGGING_CLASSES = ['ring-2', 'ring-indigo-300']

export default class extends Controller {
  static targets = ['handle']
  static values = { url: String, width: String, height: String }

  connect () {
    this.pointerDown = this.start.bind(this)
    this.pointerMove = this.resize.bind(this)
    this.pointerUp = this.stop.bind(this)
    this.pointerCancel = this.cancel.bind(this)
    if (this.hasHandleTarget) {
      this.handleTarget.addEventListener('pointerdown', this.pointerDown)
    }
  }

  disconnect () {
    if (this.hasHandleTarget) {
      this.handleTarget.removeEventListener('pointerdown', this.pointerDown)
    }
    this.removeWindowListeners()
  }

  start (event) {
    if (event.button !== 0 || !event.isPrimary) return // main button, first touch only

    const grid = this.element.parentElement
    const gridStyle = window.getComputedStyle(grid)
    const rowHeight = parseFloat(gridStyle.gridAutoRows)
    if (isNaN(rowHeight)) return // grid spans only apply from the lg breakpoint up

    event.preventDefault()
    this.initialWidth = this.widthValue
    this.initialHeight = this.heightValue
    this.startRect = this.element.getBoundingClientRect()
    this.columnGap = parseFloat(gridStyle.columnGap) || 0
    this.rowGap = parseFloat(gridStyle.rowGap) || 0
    this.columnUnit = (grid.clientWidth - this.columnGap * 5) / 6
    this.rowUnit = rowHeight
    this.element.classList.add(...DRAGGING_CLASSES)
    window.addEventListener('pointermove', this.pointerMove)
    window.addEventListener('pointerup', this.pointerUp)
    window.addEventListener('pointercancel', this.pointerCancel)
  }

  resize (event) {
    this.widthValue = this.closestSize(WIDTH_SPANS, event.clientX - this.startRect.left, this.columnUnit, this.columnGap)
    this.heightValue = this.closestSize(HEIGHT_SPANS, event.clientY - this.startRect.top, this.rowUnit, this.rowGap)
    this.applyPreview()
  }

  stop () {
    this.removeWindowListeners()
    this.element.classList.remove(...DRAGGING_CLASSES)
    if (this.widthValue === this.initialWidth && this.heightValue === this.initialHeight) return

    patch(this.urlValue, {
      body: { dashboard_panel: { width: this.widthValue, height: this.heightValue } },
      responseKind: 'turbo-stream'
    }).then((response) => {
      if (!response.ok) this.revert()
    }).catch(() => this.revert()) // network failure: nothing was persisted
  }

  // The browser can abort a drag itself (touch handed to scrolling, window
  // losing focus): drop the preview and keep the panel at its saved size.
  cancel () {
    this.removeWindowListeners()
    this.element.classList.remove(...DRAGGING_CLASSES)
    this.revert()
  }

  revert () {
    this.widthValue = this.initialWidth
    this.heightValue = this.initialHeight
    this.applyPreview()
  }

  removeWindowListeners () {
    window.removeEventListener('pointermove', this.pointerMove)
    window.removeEventListener('pointerup', this.pointerUp)
    window.removeEventListener('pointercancel', this.pointerCancel)
  }

  // The size whose pixel extent (n grid units plus the gaps between them)
  // is closest to where the pointer is — this is what makes it snap.
  closestSize (spans, targetSize, unit, gap) {
    return Object.keys(spans).reduce((best, size) => {
      const extent = (n) => n * unit + (n - 1) * gap
      return Math.abs(targetSize - extent(spans[size])) < Math.abs(targetSize - extent(spans[best])) ? size : best
    })
  }

  applyPreview () {
    this.element.classList.remove(...Object.values(WIDTH_CLASSES), ...Object.values(HEIGHT_CLASSES))
    this.element.classList.add(WIDTH_CLASSES[this.widthValue], HEIGHT_CLASSES[this.heightValue])
  }
}
